<template>
    <div
        class="ticket notranslate dynamicMarketTicket"
        v-bind:class="{ live: isLive }"
    >
        <div class="ticket_header">
            <div class="ticket_header_title">
                <span v-tooltip="waitTooltipText" class="oddsDataStates">
                    <span class="oddsDataStates_light" />
                </span>
                <strong class="optionTitle">{{ ticketTitle }}</strong>
            </div>
            <span
                class="ticket_header_cancel"
                data-testid="ticket-close"
                v-on:click="cancelTicket()"
            >
                <SvgIcon name="icon-system-close" />
            </span>
        </div>
        <div class="ticket_option">
            <div class="optionContent">
                <div class="optionContent_inner">
                    <div class="optionTeam">
                        {{ optionName }}
                    </div>
                    <div class="optionOddsGroup notranslate">
                        <span class="optionOdds optionOdds-price" v-bind:class="{ 'optionOdds-change': isPriceChange }">
                            <del v-if="isPriceChange" class="optionOddsPrice-old">{{ oldPriceDisplay }}</del>
                            <strong
                                class="optionOddsPrice"
                                data-testid="ticket-price"
                                v-bind:class="{ 'optionOddsPrice-negative': isNegativePrice }"
                            >{{ priceDisplay }}</strong>
                            <div
                                v-if="isPriceChange"
                                class="oddsStates"
                                v-bind:class="oddsStatesClass"
                            >
                                <div class="triangle" />
                            </div>
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div class="ticket_detail">
            <div class="dynamicMarketTicket_detail">
                <span>{{ homeTeamName }} -{{ vsText }}- {{ awayTeamName }}</span>
            </div>
            <div class="dynamicMarketTicket_detail">
                <span>{{ leagueName }}</span>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import type { PropType } from 'vue';
    import { computed, toRefs } from 'vue';
    import { DynamicMarketTicketContext } from '@/components/betSlip/DynamicMarketTicketContext';
    import { BetSlipContext } from '@/components/betSlip/betSlipContext';
    import SvgIcon from '@/components/common/SvgIcon.vue';
    import { i18n } from '@/core/lib/i18n';
    import { getTranslated } from '@/core/lib/translationHelper';
    import { VTooltip as vTooltip } from '@/directives/tooltip';
    import type { IDynamicMarketBetSlip } from '@/interface/IDynamicMarketBetSlip';
    import { PriceChangeStatus } from '@/interface/enum';

    const props = defineProps({
        ticket: {
            type: Object as PropType<IDynamicMarketBetSlip>,
            required: true,
        },
    });

    const { ticket } = toRefs(props);

    const {
        optionName,
        priceDisplay,
        oldPriceDisplay,
        isPriceChange,
        priceChangeStatus,
        isNegativePrice,
    } = DynamicMarketTicketContext.provide(ticket);
    const { cancelTicket } = BetSlipContext.inject();

    const isLive = computed(() => ticket.value.isLive);

    const ticketTitle = computed(() => {
        const sportName = i18n.t(`sport_type_name_${ticket.value.display.sportType}`);
        return `${sportName} - ${ticket.value.display.marketName}`;
    });

    const homeTeamName = computed(() => getTranslated(ticket.value.display.homeTeamName));
    const awayTeamName = computed(() => getTranslated(ticket.value.display.awayTeamName));
    const leagueName = computed(() => getTranslated(ticket.value.display.leagueName));

    const vsText = computed(() => i18n.t('vs'));
    const waitTooltipText = computed(() => i18n.t('wait_tooltip'));
    const oddsStatesClass = computed(() => ({
        'oddsStates-rise': priceChangeStatus.value === PriceChangeStatus.Rise,
        'oddsStates-drop': priceChangeStatus.value === PriceChangeStatus.Drop,
    }));
</script>

<style lang="scss" scoped>
    @import "@/components/betSlip/Ticket";
    @import "@/components/betSlip/DynamicMarketBetSlip";
</style>
